import {
  buildPaginatedUrlForAdapter,
  buildSearchUrlForAdapter,
  getAdapter,
  getDefaultAdapterConfig,
  hasAdapter,
} from "@repo/adapter-definitions";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "@repo/ui/chart";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { Textarea } from "@repo/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Globe,
  Loader2,
  MapPin,
  RefreshCw,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { AdapterParametersForm } from "@/components/adapters/AdapterParametersForm";
import { AdapterProviderSettingsCard } from "@/components/adapters/AdapterProviderSettingsCard";
import { DiscoverSamplePanel } from "@/components/adapters/DiscoverSamplePanel";
import { ReparseSnapshotsCard } from "@/components/adapters/ReparseSnapshotsCard";
import { useToast } from "@/hooks/use-toast";
import {
  fetchAdapterBySlug,
  fetchAdapterDailyScrapeStats,
  fetchAdapterDetailStats,
  fetchAdapterRecentRuns,
  fetchRecentScrapeQueueForAdapter,
} from "@/lib/api";

const MAX_PAGINATION_PREVIEW = 10;
const QUEUE_PAGE_SIZE = 25;

const VALID_TABS = new Set([
  "overview",
  "config",
  "urls",
  "discover",
  "queue",
  "analytics",
]);

function mergeAdapterConfig(slug: string, override: Record<string, unknown>): Record<string, unknown> {
  const base = getDefaultAdapterConfig(slug) as Record<string, unknown>;
  return { ...base, ...override };
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === "completed" ? "default"
    : status === "failed" ? "destructive"
    : status === "running" || status === "in_progress" || status === "processing" ? "secondary"
    : "outline";
  return <Badge variant={variant}>{status}</Badge>;
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const AdapterDetail = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [configJson, setConfigJson] = useState("{}");
  const [locality, setLocality] = useState("TOORAK");
  const [postcode, setPostcode] = useState("3142");
  const [state, setState] = useState("VIC");
  const [maxPages, setMaxPages] = useState(3);
  const [queueStatusFilter, setQueueStatusFilter] = useState<string | null>(null);
  const [queuePage, setQueuePage] = useState(0);

  const requestedTab = searchParams.get("tab") ?? "overview";
  const activeTab = VALID_TABS.has(requestedTab) ? requestedTab : "overview";

  const handleTabChange = useCallback(
    (next: string) => {
      const params = new URLSearchParams(searchParams);
      if (next === "overview") {
        params.delete("tab");
      } else {
        params.set("tab", next);
      }
      // When leaving Discover & Sample, drop the cross-tab handoff params so
      // a future visit doesn't auto-replay a stale preview.
      if (next !== "discover") {
        params.delete("previewUrl");
        params.delete("previewMode");
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Prefill from query params so the "Preview discovery" button on the
  // Job Details → Config tab can hand us a concrete suburb + adapter
  // config. Only runs once on mount to avoid clobbering subsequent edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: prefill is intentionally mount-only
  useEffect(() => {
    const qLocality = searchParams.get("locality");
    const qPostcode = searchParams.get("postcode");
    const qState = searchParams.get("state");
    const qMaxPages = searchParams.get("maxPages");
    const qConfig = searchParams.get("config");
    if (qLocality) setLocality(qLocality);
    if (qPostcode) setPostcode(qPostcode);
    if (qState) setState(qState);
    if (qMaxPages) {
      const parsed = parseInt(qMaxPages, 10);
      if (!Number.isNaN(parsed) && parsed > 0) setMaxPages(parsed);
    }
    if (qConfig) {
      try {
        setConfigJson(JSON.stringify(JSON.parse(qConfig), null, 2));
      } catch {
        // Bad config payload — leave the default {}.
      }
    }
  }, []);

  const registryAdapter = useMemo(() => (hasAdapter(slug) ? getAdapter(slug) : undefined), [slug]);

  const { data: adapter, isLoading, error: adapterError, refetch: refetchAdapter } = useQuery({
    queryKey: ["adapter", slug],
    queryFn: () => fetchAdapterBySlug(slug),
    enabled: Boolean(slug),
  });

  const { data: stats, error: statsError } = useQuery({
    queryKey: ["adapter-stats", adapter?.id],
    queryFn: () => fetchAdapterDetailStats(adapter?.id ?? ""),
    enabled: Boolean(adapter?.id),
  });

  const { data: dailyStats = [], error: dailyStatsError } = useQuery({
    queryKey: ["adapter-daily-stats", adapter?.id],
    queryFn: () => fetchAdapterDailyScrapeStats(adapter?.id ?? ""),
    enabled: Boolean(adapter?.id),
  });

  const { data: recentRuns = [], error: recentRunsError } = useQuery({
    queryKey: ["adapter-recent-runs", adapter?.id],
    queryFn: () => fetchAdapterRecentRuns(adapter?.id ?? ""),
    enabled: Boolean(adapter?.id),
  });

  const { data: queueRows = [], error: queueError, refetch: refetchQueue } = useQuery({
    queryKey: ["adapter-queue", adapter?.id],
    queryFn: () => fetchRecentScrapeQueueForAdapter(adapter?.id ?? "", 200),
    enabled: Boolean(adapter?.id),
  });

  const filteredQueue = useMemo(() => {
    const rows = queueStatusFilter
      ? queueRows.filter((r) => r.status === queueStatusFilter)
      : queueRows;
    return rows;
  }, [queueRows, queueStatusFilter]);

  const pagedQueue = useMemo(
    () => filteredQueue.slice(queuePage * QUEUE_PAGE_SIZE, (queuePage + 1) * QUEUE_PAGE_SIZE),
    [filteredQueue, queuePage],
  );

  const queueTotalPages = Math.max(1, Math.ceil(filteredQueue.length / QUEUE_PAGE_SIZE));

  const parsedConfig = useMemo((): Record<string, unknown> | null => {
    try {
      const raw = JSON.parse(configJson || "{}");
      if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return {};
      return raw as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [configJson]);

  const mergedConfig = useMemo(() => {
    if (!slug || !parsedConfig) return null;
    return mergeAdapterConfig(slug, parsedConfig);
  }, [slug, parsedConfig]);

  const suburbInput = useMemo(
    () => ({
      locality: locality.trim(),
      postcode: postcode.trim(),
      state: state.trim().toUpperCase(),
    }),
    [locality, postcode, state],
  );

  const urlMapping = useMemo(() => {
    if (!slug || !mergedConfig || !registryAdapter) return null;
    try {
      const searchUrl = buildSearchUrlForAdapter(slug, suburbInput, mergedConfig);
      const cap = Math.min(Math.max(1, maxPages), MAX_PAGINATION_PREVIEW);
      const pages: { page: number; url: string }[] = [{ page: 1, url: searchUrl }];
      for (let p = 2; p <= cap; p++) {
        pages.push({ page: p, url: buildPaginatedUrlForAdapter(slug, searchUrl, p, mergedConfig) });
      }
      return { searchUrl, pages };
    } catch (err) {
      return { error: err instanceof Error ? err.message : "URL build failed" };
    }
  }, [slug, mergedConfig, suburbInput, maxPages, registryAdapter]);

  const initFromAdapter = useCallback(() => {
    if (!slug || !adapter) return;
    try {
      setConfigJson(JSON.stringify(getDefaultAdapterConfig(slug), null, 2));
    } catch {
      setConfigJson("{}");
    }
  }, [slug, adapter]);

  useEffect(() => {
    if (!adapter || !slug || !hasAdapter(slug)) return;
    try {
      setConfigJson(JSON.stringify(getDefaultAdapterConfig(slug), null, 2));
    } catch {
      setConfigJson("{}");
    }
  }, [adapter, slug]);

  const handleQueueRowClick = useCallback(
    (url: string) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "discover");
      params.set("previewUrl", url);
      params.set("previewMode", "detail");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleCopyUrl = useCallback(
    async (url: string) => {
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: "Copied", description: "URL copied to clipboard." });
      } catch {
        toast({
          title: "Copy failed",
          description: "Clipboard access denied.",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  if (!slug) return <Navigate to="/dashboard/adapters" replace />;
  if (!hasAdapter(slug)) {
    return (
      <div className="space-y-4">
        <Link to="/dashboard/adapters" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-1" /> Adapters
        </Link>
        <p className="text-muted-foreground">Unknown adapter slug: {slug}</p>
      </div>
    );
  }
  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }
  if (adapterError) return <p className="text-destructive">Could not load adapter: {adapterError.message}</p>;
  if (!adapter) {
    return (
      <div className="space-y-4">
        <Link to="/dashboard/adapters" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-1" /> Adapters
        </Link>
        <p className="text-muted-foreground">Adapter not found in database: {slug}</p>
      </div>
    );
  }

  const patternLabel = registryAdapter
    ? `/${registryAdapter.meta.urlPattern.source}/${registryAdapter.meta.urlPattern.flags || ""}`
    : "—";

  const chartConfig = {
    completed: { label: "Completed", color: "var(--chart-1)" },
    failed: { label: "Failed", color: "var(--chart-2)" },
    outOfRange: { label: "Out of range", color: "var(--chart-3)" },
  };

  const previewUrlParam = searchParams.get("previewUrl") ?? undefined;
  const previewModeParam = searchParams.get("previewMode");
  const initialMode = previewModeParam === "discovery" || previewModeParam === "detail"
    ? previewModeParam
    : null;
  const listingUrlsForPicker = urlMapping && !("error" in urlMapping) ? urlMapping.pages : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/dashboard/adapters" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Adapters
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{adapter.name}</h1>
            <Badge variant={adapter.status === "active" ? "default" : "secondary"}>
              {adapter.status}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            <span className="font-mono">{adapter.slug}</span>
            {adapter.base_url && (
              <>
                {" · "}
                <a href={adapter.base_url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1">
                  {new URL(adapter.base_url).hostname} <ExternalLink className="w-3 h-3" />
                </a>
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={initFromAdapter}>
            Reset config from defaults
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
          <TabsTrigger value="urls">URL builder</TabsTrigger>
          <TabsTrigger value="discover">Discover &amp; sample</TabsTrigger>
          <TabsTrigger value="queue">Queue history</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* ===== OVERVIEW TAB ===== */}
        <TabsContent value="overview" className="space-y-4">
          {(statsError || recentRunsError) && <p className="text-sm text-destructive">Adapter statistics unavailable: {String(statsError ?? recentRunsError)}</p>}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">URLs scraped</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.totalUrlsScraped ?? "—"}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Discovery tasks</CardTitle>
                <Globe className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.totalDiscoveryTasks ?? "—"}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats ? `${stats.completedTasks} completed · ${stats.failedTasks} failed` : ""}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Success rate</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats ? `${(stats.successRate * 100).toFixed(0)}%` : "—"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Last scraped</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatRelativeTime(stats?.lastRunAt ?? null)}</div>
              </CardContent>
            </Card>
          </div>

          {adapter.description && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Description</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-muted-foreground">{adapter.description}</p></CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Adapter details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Source type</p>
                <p className="font-medium">{registryAdapter?.meta.sourceType ?? "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Supported states</p>
                <p className="font-medium">
                  {adapter.supported_states?.join(", ") ?? "All"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">URL pattern</p>
                <code className="text-xs break-all">{patternLabel}</code>
              </div>
              <div>
                <p className="text-muted-foreground">Out-of-range rate</p>
                <p className="font-medium">
                  {stats ? `${(stats.outOfRangeRate * 100).toFixed(1)}%` : "—"}
                </p>
              </div>
            </CardContent>
          </Card>

          <ReparseSnapshotsCard adapterSlug={adapter.slug} />

          {recentRuns.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Recent job runs</CardTitle>
                <CardDescription>Last {recentRuns.length} runs using this adapter</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentRuns.map((run) => (
                    <div key={run.id} className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <StatusBadge status={run.phase} />
                        <div>
                          <p className="text-sm font-medium">{run.job_name}</p>
                          <p className="text-xs text-muted-foreground">{formatRelativeTime(run.started_at)}</p>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-mono">{run.urls_scraped} URLs</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== CONFIGURATION TAB ===== */}
        <TabsContent value="config" className="space-y-4">
          <AdapterParametersForm
            adapter={adapter}
            onSaved={() => refetchAdapter()}
          />
          <AdapterProviderSettingsCard adapterSlug={adapter.slug} />
        </TabsContent>

        {/* ===== URL BUILDER TAB ===== */}
        <TabsContent value="urls" className="space-y-4">
          <div className="card-elevated p-4 space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium flex items-center gap-2"><MapPin className="w-4 h-4" /> Suburb (for listing URL)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="vis-locality">Locality</Label>
                  <Input id="vis-locality" name="vis-locality" value={locality} onChange={(e) => setLocality(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vis-postcode">Postcode</Label>
                  <Input id="vis-postcode" name="vis-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vis-state">State</Label>
                  <Input id="vis-state" name="vis-state" value={state} onChange={(e) => setState(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adapter-url-config">Adapter config (JSON, merged with defaults)</Label>
              <Textarea id="adapter-url-config" name="adapter-url-config" value={configJson} onChange={(e) => setConfigJson(e.target.value)} rows={6} className="font-mono text-xs" />
              {!parsedConfig && <p className="text-xs text-destructive">Invalid JSON — fix before building URLs.</p>}
            </div>
            <div className="space-y-1 max-w-xs">
              <Label htmlFor="vis-max-pages">Max paginated pages (2…{MAX_PAGINATION_PREVIEW})</Label>
              <Input id="vis-max-pages" name="vis-max-pages" type="number" min={1} max={MAX_PAGINATION_PREVIEW} value={maxPages} onChange={(e) => setMaxPages(parseInt(e.target.value, 10) || 1)} />
            </div>
            <div className="space-y-1 text-sm">
              <p><span className="text-muted-foreground">Property URL pattern: </span><code className="text-xs break-all">{patternLabel}</code></p>
            </div>
            {urlMapping && "error" in urlMapping && <p className="text-sm text-destructive">{urlMapping.error}</p>}
            {urlMapping && !("error" in urlMapping) && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Listing / search URLs</p>
                  <ul className="space-y-2 text-xs font-mono break-all">
                    {urlMapping.pages.map((p) => (
                      <li key={p.page} className="border rounded p-2 bg-muted/20">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 break-all">
                            <span className="text-muted-foreground mr-2">Page {p.page}:</span>{p.url}
                          </div>
                          <div className="flex shrink-0 gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyUrl(p.url)}
                              aria-label="Copy URL"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
                              aria-label="Open in new tab"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                        <div className="mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const params = new URLSearchParams(searchParams);
                              params.set("tab", "discover");
                              params.set("previewUrl", p.url);
                              params.set("previewMode", "discovery");
                              setSearchParams(params, { replace: true });
                            }}
                          >
                            Dry-run discovery on this URL
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== DISCOVER & SAMPLE TAB ===== */}
        <TabsContent value="discover" className="space-y-4">
          <DiscoverSamplePanel
            adapterSlug={slug}
            configJson={configJson}
            onConfigJsonChange={setConfigJson}
            initialUrl={previewUrlParam}
            initialMode={initialMode}
            listingUrls={listingUrlsForPicker}
          />
        </TabsContent>

        {/* ===== QUEUE HISTORY TAB ===== */}
        <TabsContent value="queue" className="space-y-4">
          {queueError && <p className="text-sm text-destructive">Queue history unavailable: {queueError.message}</p>}
          <div className="card-elevated p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-1">
                {[null, "pending", "in_progress", "completed", "failed"].map((s) => (
                  <Button
                    key={s ?? "all"}
                    type="button"
                    variant={queueStatusFilter === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setQueueStatusFilter(s); setQueuePage(0); }}
                  >
                    {s ?? "All"}
                    {s === null && <span className="ml-1 text-xs opacity-70">({queueRows.length})</span>}
                  </Button>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => refetchQueue()}>
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
            {pagedQueue.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No queue rows match the filter.</p>
            ) : (
              <>
                <div className="border rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30 text-left">
                        <th className="p-2 font-medium">Status</th>
                        <th className="p-2 font-medium">URL</th>
                        <th className="p-2 font-medium">Created</th>
                        <th className="p-2 font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedQueue.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b last:border-0 hover:bg-muted/20 cursor-pointer"
                          onClick={() => handleQueueRowClick(row.url)}
                        >
                          <td className="p-2 whitespace-nowrap">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="p-2 max-w-md truncate font-mono text-xs text-primary underline-offset-2 hover:underline" title={row.url}>
                            {row.url}
                          </td>
                          <td className="p-2 whitespace-nowrap text-muted-foreground text-xs">
                            {formatRelativeTime(row.created_at)}
                          </td>
                          <td className="p-2 text-destructive text-xs max-w-xs truncate" title={row.last_error ?? ""}>
                            {row.last_error ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {queueTotalPages > 1 && (
                  <div className="flex items-center justify-between text-sm">
                    <p className="text-muted-foreground">
                      Page {queuePage + 1} of {queueTotalPages} ({filteredQueue.length} rows)
                    </p>
                    <div className="flex gap-1">
                      <Button type="button" variant="outline" size="sm" disabled={queuePage === 0} onClick={() => setQueuePage((p) => p - 1)}>
                        Previous
                      </Button>
                      <Button type="button" variant="outline" size="sm" disabled={queuePage >= queueTotalPages - 1} onClick={() => setQueuePage((p) => p + 1)}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        {/* ===== ANALYTICS TAB ===== */}
        <TabsContent value="analytics" className="space-y-4">
          {dailyStatsError && <p className="text-sm text-destructive">Analytics unavailable: {dailyStatsError.message}</p>}
          {dailyStats.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No scrape data available yet for this adapter.
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Scrape volume (last 30 days)</CardTitle>
                  <CardDescription>Completed vs failed scrapes per day</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={chartConfig} className="h-[280px] w-full">
                    <BarChart data={dailyStats} accessibilityLayer>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="date" tickFormatter={(d: string) => d.slice(5)} fontSize={11} />
                      <YAxis fontSize={11} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="completed" stackId="a" fill="var(--color-completed)" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="failed" stackId="a" fill="var(--color-failed)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Out-of-range rate (last 30 days)</CardTitle>
                  <CardDescription>Scrapes flagged as outside the configured date window</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={chartConfig} className="h-[220px] w-full">
                    <LineChart data={dailyStats} accessibilityLayer>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="date" tickFormatter={(d: string) => d.slice(5)} fontSize={11} />
                      <YAxis fontSize={11} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line type="monotone" dataKey="outOfRange" stroke="var(--color-outOfRange)" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdapterDetail;
